import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { toast } from 'react-toastify';
import VoiceChannelsService from 'services/ServiceVOICECHANNELS';
import * as yup from 'yup';

const createPayloadSchema = yup.object().shape({
  name: yup.string().required('O nome do canal é obrigatório'),
  description: yup.string().nullable()
});

const useVoiceChannels = (props = {}) => {
  const { itemsPerPage = 10 } = props;

  const [state, setState] = useState({
    response: [],
    isLoading: false,
    isError: false,
    error: null,
    totalRegisters: 0,
    activePage: 1,
    searchText: ''
  });

  const hasInitialFetch = useRef(false);
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef(null);

  const options = useMemo(
    () => state.response.map(({ id, name }) => ({ label: name, value: id })),
    [state.response]
  );

  const safeSetState = useCallback((updater) => {
    if (isMountedRef.current) {
      setState(updater);
    }
  }, []);

  const fetchData = useCallback(
    async (selectedPage, search) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();

      safeSetState((prevState) => ({
        ...prevState,
        isLoading: true,
        isError: false,
        error: null
      }));

      try {
        const response = await VoiceChannelsService.list(
          selectedPage,
          itemsPerPage,
          search
        );

        if (!isMountedRef.current) return;

        const rawData = response.data;
        let dataToList = [];
        let total = 0;

        if (Array.isArray(rawData)) {
          dataToList = rawData;
          total = rawData.length;
        } else if (rawData && rawData.data) {
          dataToList = rawData.data;
          total =
            rawData.totalRegisters ||
            (Array.isArray(rawData.data) ? rawData.data.length : 0);
        } else {
          dataToList = [];
        }

        if (!isMountedRef.current) return;

        safeSetState((prevState) => ({
          ...prevState,
          response: dataToList,
          totalRegisters: total,
          isLoading: false,
          activePage: selectedPage,
          searchText: search
        }));
      } catch (err) {
        if (err.name === 'AbortError' || !isMountedRef.current) return;

        console.error('Erro no fetch de canais:', err);
        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          'Erro ao carregar canais de voz.';

        toast.error(errorMessage);

        safeSetState((prevState) => ({
          ...prevState,
          response: [],
          isLoading: false,
          isError: true,
          error: err
        }));
      }
    },
    [itemsPerPage, safeSetState]
  );

  const fetch = useCallback(
    (page, search) => {
      fetchData(page, search);
    },
    [fetchData]
  );

  const handlePageChange = useCallback(
    (page) => {
      fetch(page, state.searchText);
    },
    [fetch, state.searchText]
  );

  const handleSearchChange = useCallback(
    (value) => {
      safeSetState((prevState) => ({ ...prevState, searchText: value }));
      fetch(1, value);
    },
    [fetch, safeSetState]
  );

  const getChannelById = useCallback(async (id) => {
    try {
      const response = await VoiceChannelsService.findById(id);
      const data =
        response.data && response.data.data
          ? response.data.data
          : response.data;
      return data;
    } catch (err) {
      console.error('Erro ao buscar canal:', err);
      const msg =
        err.response?.data?.message || 'Erro ao buscar detalhes do canal.';
      toast.error(msg);
      return null;
    }
  }, []);

  const createChannel = useCallback(async (data) => {
    try {
      await createPayloadSchema.validate(data);
      const response = await VoiceChannelsService.create(data);

      if (response.data && response.data.status === 'ERROR') {
        throw new Error(response.data.message);
      }

      toast.success('Canal criado com sucesso!');
      return true;
    } catch (err) {
      console.error('Erro ao criar:', err);
      const msg =
        err.name === 'ValidationError'
          ? err.message
          : err.response?.data?.message || err.message || 'Erro ao criar.';
      toast.error(msg);
      return false;
    }
  }, []);

  const updateChannel = useCallback(async (id, data) => {
    try {
      await createPayloadSchema.validate(data);
      const response = await VoiceChannelsService.update(id, data);

      if (response.data && response.data.status === 'ERROR') {
        throw new Error(response.data.message);
      }

      toast.success('Canal atualizado com sucesso!');
      return true;
    } catch (err) {
      console.error('Erro ao atualizar:', err);
      const msg =
        err.name === 'ValidationError'
          ? err.message
          : err.response?.data?.message || err.message || 'Erro ao atualizar.';
      toast.error(msg);
      return false;
    }
  }, []);

  const deleteChannel = useCallback(
    async (id) => {
      try {
        const response = await VoiceChannelsService.delete(id);
        if (response.data && response.data.status === 'ERROR') {
          throw new Error(response.data.message);
        }

        toast.success('Canal removido.');

        if (isMountedRef.current) {
          const currentActivePage = state.activePage;
          const currentSearch = state.searchText;
          fetchData(currentActivePage, currentSearch);
        }

        return true;
      } catch (err) {
        toast.error(err.response?.data?.message || 'Erro ao remover canal.');
        return false;
      }
    },
    [fetchData, state.activePage, state.searchText]
  );

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    if (!hasInitialFetch.current) {
      hasInitialFetch.current = true;
      fetchData(1, '');
    }
  }, [fetchData]);

  return {
    ...state,
    options,
    fetch,
    handlePageChange,
    handleSearchChange,
    getChannelById,
    createChannel,
    updateChannel,
    deleteChannel
  };
};

export default useVoiceChannels;
